<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.all_synatec_orders')" active></b-tab>
        </b-tabs>
        <div class="tabs-header-actions">
          <button
            class="action-btn export-btn"
            @click="exportModal.show = true"
          >
            <v-icon name="file-download"></v-icon>
            {{ $t('globals.export').toUpperCase() }}
          </button>
        </div>
      </div>
      <div class="tab-content">
        <div class="tab-content-inner">
          <!-- Search and Filters Section -->
          <div class="filters-section">
            <div class="search-container">
              <label class="search-label">{{ $t("globals.customer") }}</label>
              <b-form-input
                v-model="searchQuery.customerName"
                @keyup.enter="searchOrdersLikeCustomerName"
                class="search-input"
                :placeholder="$t('globals.search_for') + '...'"
                :disabled="getAllOrdersLoading"
              />
            </div>

            <div class="filter-group">
              <div class="filter-item">
                <label class="filter-label">{{ $t("globals.status") }}</label>
                <b-form-select
                  v-model="searchQuery.status"
                  :options="statusOptions"
                  class="filter-select"
                />
              </div>
            </div>

            <div class="action-buttons">
              <b-button
                variant="primary"
                class="search-btn"
                @click="searchOrdersLikeCustomerName"
                :disabled="getAllOrdersLoading"
              >
                {{ $t("globals.search").toUpperCase() }}
              </b-button>
              <button
                class="reload-btn"
                @click="resetReload"
                :disabled="getAllOrdersLoading"
                v-b-tooltip.hover
                :title="$t('globals.clear_filters')"
              >
                <icon name="sync-alt" :spin="getAllOrdersLoading"></icon>
              </button>
            </div>
          </div>

          <!-- Table -->
          <div class="table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    :key="field.key || field"
                    :class="{ sortable: field.sortable }"
                    @click="field.sortable && sortByField(field.key || field)"
                  >
                    {{ field.label || field }}
                    <span v-if="sortBy === (field.key || field)" class="sort-icon">
                      {{ sortDesc ? '▼' : '▲' }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in getAllOrders"
                  :key="item.id"
                  class="table-row"
                  @click="rowClicked(item)"
                >
                  <td>{{ item.id }}</td>
                  <td>{{ item.agent ? item.agent.full_name : '-' }}</td>
                  <td>
                    <router-link
                      v-if="item.customer"
                      class="customer-link"
                      :to="{name: 'customer', params:{ id: item.customer.id }}"
                      @click.native.stop
                      target="_blank"
                    >
                      {{ item.customer.company_name }}
                    </router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="item.order"
                      class="customer-link"
                      :to="{ name: 'order', params: { id: item.order.id } }"
                      @click.native.stop
                      target="_blank"
                    >
                      {{ item.order.order_no }}
                    </router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <span class="status-badge" :class="`status-${item.status}`">
                      {{ $t('globals.' + item.status) }}
                    </span>
                  </td>
                  <td v-b-tooltip.hover :title="item.created_at">
                    {{ item.created_at ? item.created_at.substring(0, 10) : '-' }}
                  </td>
                  <td v-b-tooltip.hover :title="item.notes">
                    {{
                      item.notes && item.notes.length > 20
                        ? item.notes.substring(0, 10) + "..."
                        : item.notes || '-'
                    }}
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="getAllOrdersLoading" class="loading-state">
              <div class="spinner"></div>
              <p>{{ $t("globals.loading") }}...</p>
            </div>
            <div v-else-if="getAllOrders.length === 0" class="empty-state">
              <p>{{ $t("globals.no_results") }}</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-section">
            <div class="controls-left">
              <span class="summary-label">{{ $t("globals.showing") }} {{ offset + 1 }}-{{ Math.min(offset + limit, offset + getAllOrders.length) }}</span>
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="offset === 0 || getAllOrdersLoading"
                @click="fetchLess"
              >
                &lt;
              </button>
              <button
                class="page-btn"
                :disabled="getAllOrders.length < limit || getAllOrdersLoading"
                @click="fetchMore"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <ulu-synatec-orders-export-modal
      :show="exportModal.show"
      :close="closeModals"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { currencyFormat } from "@/helpers/price";

import "vue-awesome/icons/sync-alt";
import "vue-awesome/icons/file-download";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
    "ulu-synatec-orders-export-modal": () =>
      import("@/components/synatec_orders/SynatecOrdersExportModal.vue")
  },
  data() {
    return {
      exportModal: {
        show: false
      },
      statusOptions: [
        { text: 'All', value: null },
        { text: 'Created', value: 'created' },
        { text: 'Requested', value: 'requested' },
        { text: "Fulfilled", value: "fulfilled" },
        { text: 'Canceled', value: 'canceled' },
      ],
      searchQuery: {
        agentName: null,
        customerName: null,
        status: null
      },
      offset: 0,
      limit: 20,
      sortBy: "created_at",
      sortDesc: true,
      fields: [
        "id",
        {
          key: "agent.full_name",
          label: this.$t("globals.agent"),
        },
        {
          key: "customer",
          label: this.$t("globals.customer"),
          sortable: true
        },
        {
          key: "order",
          label: this.$t("globals.order"),
        },
        {
          key: "status",
          label: this.$t("globals.status"),
        },
        {
          key: "created_at",
          label: this.$t("globals.creation_date"),
          sortable: true
        },
        {
          key: "notes",
          label: this.$t("globals.notes"),
        },
      ]
    };
  },
  computed: {
    ...mapGetters("synatec_orders", ["getAllOrders", "getAllOrdersLoading"]),
  },
  methods: {
    currencyFormat,
    ...mapActions("synatec_orders", [
      "fetchOrdersPagination",
      "fetchOrdersLikeCustomer"
    ]),
    rowClicked(item) {
      // Don't navigate if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      this.$router.push({ name: "synatecorder", params: { id: item.id } });
    },
    sortByField(key) {
      if (this.sortBy === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = key;
        this.sortDesc = false;
      }
      this.fetch();
    },
    fetchMore() {
      this.offset += 20;
      this.selectFetch();
    },
    fetchLess() {
      if (this.offset >= 20) {
        this.offset -= 20;
        this.selectFetch();
      }
    },
    resetOffsetSort() {
      this.offset = 0;
      this.sortBy = "created_at";
      this.sortDesc = true;
    },
    resetQuery() {
      this.searchQuery.agentName = null;
      this.searchQuery.customerName = null;
      this.searchQuery.status = null;
    },
    fetch() {
      let order_by = this.sortBy;
      // Customer exception
      if (order_by === 'customer') {
        order_by = 'customer_id';
      }

      if (this.sortDesc) {
        order_by += " DESC";
      } else {
        order_by += " ASC";
      }

      this.fetchOrdersPagination({
        this_ref: this,
        status: this.searchQuery.status,
        limit: this.limit,
        offset: this.offset,
        order: order_by
      });
    },
    selectFetch() {
      if (
        this.searchQuery.agentName != null
        && this.searchQuery.agentName.length > 2
      ) {
        this.searchOrdersLikeAgentName();
      } else if (
        this.searchQuery.customerName != null
        && this.searchQuery.customerName.length > 2
      ) {
        this.searchOrdersLikeCustomerName();
      } else {
        this.fetch();
      }
    },
    searchOrdersLikeAgentName() {
      if (this.searchQuery.agentName != null && this.searchQuery.agentName.length > 2) {
        this.searchQuery.orderNo = null;
        this.searchQuery.customerName = null;

        this.fetchOrdersLikeAgent({
          agentName: this.searchQuery.agentName,
          status: this.searchQuery.status,
          this_ref: this,
          limit: this.limit,
          offset: this.offset,
        });
      }
    },
    searchOrdersLikeCustomerName() {
      if (this.searchQuery.customerName != null && this.searchQuery.customerName.length > 2) {
        this.searchQuery.orderNo = null;
        this.searchQuery.agentName = null;

        this.fetchOrdersLikeCustomer({
          customerName: this.searchQuery.customerName,
          status: this.searchQuery.status,
          this_ref: this,
          limit: this.limit,
          offset: this.offset,
        });
      }
    },
    closeModals() {
      this.exportModal.show = false;
      this.resetReload();
    },
    resetReload() {
      this.resetOffsetSort();
      this.resetQuery();
      this.fetch();
    }
  },
  watch: {
    'searchQuery.status'(newVal) {
      this.resetOffsetSort();
      this.selectFetch();
    }
  },
  mounted() {
    this.resetReload();
  }
};
</script>

<style scoped>
/* Action buttons and reload button styles are imported from modern-table.css */

/* Synatec order status badges */
.status-badge.status-confirmed {
  background-color: #e3f2fd;
  color: #1976d2;
}

.status-badge.status-fulfilled {
  background-color: #e8f5e9;
  color: #388e3c;
}

.status-badge.status-created {
  background-color: #fff3e0;
  color: #f57c00;
}

.status-badge.status-requested {
  background-color: #e1f5fe;
  color: #0288d1;
}

.status-badge.status-canceled,
.status-badge.status-cancelled {
  background-color: #ffebee;
  color: #d32f2f;
}
</style>
